import { useState, useCallback, useRef } from 'react';
import type { Message } from '../types/chat';
import { sendChatMessage } from '../lib/chatApi';

export function useChat(endpoint: string = '/api/chat') {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [error, setError] = useState<string | null>(null);
  const streamingContentRef = useRef('');

  const sendMessage = useCallback(async (content: string) => {
    if (!content.trim() || isLoading) return;

    setError(null);
    const userMessage: Message = {
      id: crypto.randomUUID(),
      role: 'user',
      content: content.trim(),
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);
    setStreamingContent('');
    streamingContentRef.current = '';

    const messagesForApi = [...messages, userMessage].map(m => ({
      role: m.role,
      content: m.content,
    }));

    await sendChatMessage(
      messagesForApi,
      (chunk) => {
        streamingContentRef.current += chunk;
        setStreamingContent(streamingContentRef.current);
      },
      () => {
        const assistantMessage: Message = {
          id: crypto.randomUUID(),
          role: 'assistant',
          content: streamingContentRef.current,
          timestamp: new Date(),
        };
        setMessages(prev => [...prev, assistantMessage]);
        setStreamingContent('');
        streamingContentRef.current = '';
        setIsLoading(false);
      },
      (errorMsg) => {
        setError(errorMsg);
        const errorMessage: Message = {
          id: crypto.randomUUID(),
          role: 'assistant',
          content: `I encountered an error: ${errorMsg}. Please try again.`,
          timestamp: new Date(),
        };
        setMessages(prev => [...prev, errorMessage]);
        setStreamingContent('');
        streamingContentRef.current = '';
        setIsLoading(false);
      },
      endpoint
    );
  }, [messages, isLoading, endpoint]);

  const clearChat = useCallback(() => {
    setMessages([]);
    setStreamingContent('');
    setError(null);
  }, []);

  return {
    messages,
    isLoading,
    streamingContent,
    error,
    sendMessage,
    clearChat,
  };
}
